import React, { useCallback, useRef, useState } from "react";
import { read as xlsxRead, utils as xlsxUtils } from "xlsx";
import { toast } from "react-toastify";
import { XMarkIcon, ArrowUpTrayIcon } from "@heroicons/react/24/outline";
import { bankTransactionsApi } from "../../services/api";
import { BankTransactionType } from "@akfdev-com/shared/src/models";

interface Props {
  buildingId: string;
  bankAccountId: string;
  bankAccountName: string;
  onClose: () => void;
  onImported: () => void;
}

interface ParsedRow {
  bankAccountName: string;
  description: string;
  date: string;
  type: string;
  grossValue: string;
  _valid: boolean;
  _errors: string[];
}

const COLUMN_ALIASES: Record<string, string> = {
  "bank account name": "bankAccountName",
  bankaccountname: "bankAccountName",
  description: "description",
  date: "date",
  type: "type",
  "gross value": "grossValue",
  grossvalue: "grossValue",
  amount: "grossValue",

  // FreeAgent-specific columns
  "receipt reference": "reference",
  "paid invoice reference": "reference",
  "cheque number": "reference",
  "category name": "categoryName",
  "nominal code": "nominalCode",
};

const validTransactionTypes = new Set<string>(
  Object.values(BankTransactionType),
);

function mapColumns(headers: string[]): Record<number, string> {
  const map: Record<number, string> = {};
  headers.forEach((h, i) => {
    const key = COLUMN_ALIASES[h.toLowerCase().trim()];
    if (key) map[i] = key;
  });
  return map;
}

function validateRow(row: ParsedRow): ParsedRow {
  const errors: string[] = [];

  if (!row.date) {
    errors.push("Missing date");
  } else if (isNaN(Date.parse(row.date))) {
    errors.push("Invalid date format");
  }

  if (!row.description) {
    errors.push("Missing description");
  }

  if (row.grossValue === "" || isNaN(parseFloat(row.grossValue))) {
    errors.push("Invalid gross value");
  }

  if (row.type && !validTransactionTypes.has(row.type)) {
    errors.push(`Unknown type "${row.type}"`);
  }

  return { ...row, _errors: errors, _valid: errors.length === 0 };
}

function normalizeDate(value: unknown): string {
  if (!value) return "";
  if (typeof value === "number") {
    // Excel serial date
    const date = new Date(Math.round((value - 25569) * 86400 * 1000));
    return date.toISOString().split("T")[0];
  }
  const s = String(value).trim();
  const parsed = new Date(s);
  if (!isNaN(parsed.getTime())) return parsed.toISOString().split("T")[0];
  return s;
}

const ImportTransactionsModal: React.FC<Props> = ({
  buildingId,
  bankAccountId,
  bankAccountName,
  onClose,
  onImported,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [rows, setRows] = useState<ParsedRow[]>([]);
  const [skipped, setSkipped] = useState<Set<number>>(new Set());
  const [importing, setImporting] = useState(false);
  const [fileName, setFileName] = useState("");

  const parseFile = useCallback(
    (file: File) => {
      setFileName(file.name);
      const reader = new FileReader();
      reader.onload = (e) => {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const wb = xlsxRead(data, { type: "array" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const raw: unknown[][] = xlsxUtils.sheet_to_json(ws, { header: 1 });

        if (raw.length < 2) {
          toast.error("The file appears to be empty.");
          return;
        }

        const headers = (raw[0] as string[]).map(String);
        const colMap = mapColumns(headers);

        const parsed: ParsedRow[] = (raw.slice(1) as unknown[][])
          .filter((row) =>
            row.some((cell) => cell !== undefined && cell !== ""),
          )
          .map((row) => {
            // Start with defaults, then map columns
            const mapped: Partial<ParsedRow & { reference?: string }> = {
              bankAccountName: bankAccountName,
              description: "",
              date: "",
              type: "",
              grossValue: "",
              reference: "",
              _valid: false,
              _errors: [],
            };
            Object.entries(colMap).forEach(([idx, key]) => {
              const val = row[parseInt(idx)];
              if (key === "date") {
                (mapped as any)[key] = normalizeDate(val);
              } else if (key === "grossValue") {
                // CSV/XLSX values are in pounds — convert to pence
                const num = parseFloat(String(val).replace(/,/g, ""));
                if (!isNaN(num)) {
                  (mapped as any)[key] = String(Math.round(num * 100));
                } else {
                  (mapped as any)[key] = "";
                }
              } else {
                (mapped as any)[key] =
                  val !== undefined ? String(val).trim() : "";
              }
            });
            // Override bankAccountName with parsed value if present, else keep default
            if (!mapped.bankAccountName)
              mapped.bankAccountName = bankAccountName;
            // If reference is present, use it in description for traceability
            if (mapped.reference) {
              mapped.description = mapped.description
                ? `${mapped.description} (${mapped.reference})`
                : mapped.reference;
            }
            return validateRow(mapped as ParsedRow);
          });

        setRows(parsed.slice(0, 500));
        setSkipped(new Set());
      };
      reader.readAsArrayBuffer(file);
    },
    [bankAccountName],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      const file = e.dataTransfer.files[0];
      if (file) parseFile(file);
    },
    [parseFile],
  );

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) parseFile(file);
  };

  const toggleSkip = (i: number) => {
    setSkipped((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  };

  const validRows = rows.filter((r, i) => r._valid && !skipped.has(i));

  const handleImport = async () => {
    if (validRows.length === 0) {
      toast.error("No valid transactions to import.");
      return;
    }

    setImporting(true);
    try {
      const transactions = validRows.map((r) => ({
        bankAccountId,
        bankAccountName: r.bankAccountName || bankAccountName,
        description: r.description,
        date: r.date,
        type: validTransactionTypes.has(r.type)
          ? r.type
          : BankTransactionType.Other,
        grossValue: parseInt(r.grossValue, 10),
        reconciled: false, // FreeAgent doesn't provide this, default to false
      }));

      await bankTransactionsApi.import(buildingId, transactions);
      toast.success(`Imported ${transactions.length} transactions.`);
      onImported();
    } catch (err) {
      console.error("Import failed:", err);
      toast.error("Import failed. Please try again.");
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-start justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        />

        <div className="relative inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-5xl w-full">
          <div className="bg-white px-6 pt-5 pb-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Import Transactions
              </h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Drop zone */}
            <div
              className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-primary-400 transition-colors"
              onDrop={handleDrop}
              onDragOver={(e) => e.preventDefault()}
              onClick={() => inputRef.current?.click()}
            >
              <ArrowUpTrayIcon className="h-10 w-10 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-600">
                Drag & drop a file here, or{" "}
                <span className="text-primary-600 font-medium">
                  click to browse
                </span>
              </p>
              <p className="text-xs text-gray-400 mt-1">
                Supports .xlsx, .xls, .csv
              </p>
              {fileName && (
                <p className="text-xs text-primary-600 mt-2 font-medium">
                  {fileName}
                </p>
              )}
              <input
                ref={inputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                className="hidden"
                onChange={handleFileChange}
              />
            </div>

            {rows.length > 0 && (
              <>
                <div className="mt-4 flex items-center justify-between">
                  <p className="text-sm text-gray-600">
                    Showing {Math.min(rows.length, 20)} of {rows.length} rows.{" "}
                    <span className="font-medium text-green-700">
                      {validRows.length} valid
                    </span>
                    ,{" "}
                    <span className="font-medium text-red-600">
                      {
                        rows.filter((r, i) => !r._valid && !skipped.has(i))
                          .length
                      }{" "}
                      invalid
                    </span>
                    {skipped.size > 0 && (
                      <span className="font-medium text-gray-500">
                        , {skipped.size} skipped
                      </span>
                    )}
                  </p>
                </div>

                <div className="mt-2 overflow-x-auto max-h-72 overflow-y-auto rounded border border-gray-200">
                  <table className="min-w-full text-xs divide-y divide-gray-200">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-gray-500">
                          Skip
                        </th>
                        <th className="px-3 py-2 text-left font-medium text-gray-500">
                          Bank Account Name
                        </th>
                        <th className="px-3 py-2 text-left font-medium text-gray-500">
                          Description
                        </th>
                        <th className="px-3 py-2 text-left font-medium text-gray-500">
                          Date
                        </th>
                        <th className="px-3 py-2 text-left font-medium text-gray-500">
                          Type
                        </th>
                        <th className="px-3 py-2 text-right font-medium text-gray-500">
                          Gross Value
                        </th>
                        <th className="px-3 py-2 text-left font-medium text-gray-500">
                          Issues
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {rows.slice(0, 20).map((row, i) => (
                        <tr
                          key={i}
                          className={`${
                            skipped.has(i)
                              ? "opacity-40 bg-gray-50"
                              : row._valid
                              ? "bg-white"
                              : "bg-red-50"
                          }`}
                        >
                          <td className="px-3 py-1.5 text-center">
                            <input
                              type="checkbox"
                              checked={skipped.has(i)}
                              onChange={() => toggleSkip(i)}
                              className="h-3.5 w-3.5 rounded border-gray-300"
                            />
                          </td>
                          <td className="px-3 py-1.5 text-gray-700">
                            {row.bankAccountName}
                          </td>
                          <td className="px-3 py-1.5 text-gray-900 max-w-xs truncate">
                            {row.description}
                          </td>
                          <td className="px-3 py-1.5 text-gray-500 whitespace-nowrap">
                            {row.date}
                          </td>
                          <td className="px-3 py-1.5 text-gray-500">
                            {row.type}
                          </td>
                          <td
                            className={`px-3 py-1.5 text-right font-mono ${
                              parseFloat(row.grossValue) < 0
                                ? "text-red-600"
                                : "text-green-700"
                            }`}
                          >
                            {row.grossValue}
                          </td>
                          <td className="px-3 py-1.5 text-red-600">
                            {row._errors.join(", ")}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>

          <div className="bg-gray-50 px-6 py-3 flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleImport}
              disabled={importing || validRows.length === 0}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md shadow-sm hover:bg-primary-700 disabled:opacity-50"
            >
              {importing
                ? "Importing…"
                : `Import ${validRows.length} transactions`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImportTransactionsModal;
